import { useEffect } from 'react';

const SITE_NAME = 'Vivat Alfa Winery';
const ORIGIN = 'https://www.vivatalfawinery.com';

// Trim to something that will not be cut off in a search result.
function truncate(text, limit) {
  const clean = String(text || '').replace(/\s+/g, ' ').trim();
  if (clean.length <= limit) return clean;
  return clean.slice(0, clean.lastIndexOf(' ', limit - 1)).trimEnd() + '…';
}

// Event dates are free text ("Saturday, April 25th"), so a start date can only
// be published when the year is actually written down. Guessing one would put
// a wrong date into machine-readable markup, which is worse than omitting it.
export function parseEventDate(text) {
  const raw = String(text || '');
  const year = raw.match(/\b(20\d{2})\b/);
  if (!year) return null;

  const parsed = new Date(raw.replace(/(\d+)(st|nd|rd|th)/gi, '$1'));
  if (Number.isNaN(parsed.getTime())) return null;

  return parsed.toISOString().split('T')[0];
}

// Applies per-page metadata and removes it again on unmount, so navigating back
// to the home page does not leave an event's tags behind.
export default function usePageMetadata({ title, description, image, path, event }) {
  useEffect(() => {
    const previousTitle = document.title;
    if (title) document.title = title;

    const added = [];

    const setMeta = (selector, attrs) => {
      let element = document.head.querySelector(selector);
      const existed = !!element;
      const previous = existed ? element.getAttribute('content') : null;

      if (!existed) {
        element = document.createElement('meta');
        Object.entries(attrs).forEach(([k, v]) => element.setAttribute(k, v));
        document.head.appendChild(element);
      } else {
        element.setAttribute('content', attrs.content);
      }

      added.push({ element, existed, previous });
    };

    if (description) {
      setMeta('meta[name="description"]', { name: 'description', content: description });
      setMeta('meta[property="og:description"]', { property: 'og:description', content: description });
    }
    if (title) {
      setMeta('meta[property="og:title"]', { property: 'og:title', content: title });
    }
    if (path) {
      setMeta('meta[property="og:url"]', { property: 'og:url', content: ORIGIN + path });
    }
    if (image) {
      setMeta('meta[property="og:image"]', { property: 'og:image', content: ORIGIN + image });
    }

    // Canonical, so an event page does not compete with the home page.
    let canonical = document.head.querySelector('link[rel="canonical"]');
    const previousCanonical = canonical ? canonical.getAttribute('href') : null;
    if (canonical && path) canonical.setAttribute('href', ORIGIN + path);

    // Event structured data, only when there is a real date to publish.
    let script = null;
    if (event) {
      const startDate = parseEventDate(event.date);
      if (startDate) {
        script = document.createElement('script');
        script.type = 'application/ld+json';
        script.textContent = JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Event',
          name: event.title,
          startDate,
          eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
          eventStatus: 'https://schema.org/EventScheduled',
          description: truncate(event.pageDescription || event.description, 300),
          image: event.image ? ORIGIN + event.image : undefined,
          url: ORIGIN + path,
          location: {
            '@type': 'Place',
            name: SITE_NAME,
            address: {
              '@type': 'PostalAddress',
              streetAddress: '3612 Stump Road',
              addressLocality: 'Doylestown',
              addressRegion: 'PA',
              postalCode: '18902',
              addressCountry: 'US',
            },
          },
          organizer: { '@type': 'Organization', name: SITE_NAME, url: ORIGIN },
        });
        document.head.appendChild(script);
      }
    }

    return () => {
      document.title = previousTitle;

      for (const { element, existed, previous } of added) {
        if (existed) element.setAttribute('content', previous);
        else element.remove();
      }

      if (canonical && previousCanonical) canonical.setAttribute('href', previousCanonical);
      if (script) script.remove();
    };
  }, [title, description, image, path, event]);
}

export { truncate, SITE_NAME, ORIGIN };
